/**
 * Shared validation utilities for consistent data validation across frontend, admin and backend
 */

/**
 * Validates if a string is a valid MongoDB ObjectID (24-character hex string)
 * 
 * @param {string} id - The ID to validate
 * @returns {boolean} - Whether the ID is valid
 */
function isValidObjectId(id) {
  if (!id || typeof id !== 'string') {
    return false;
  }
  
  return /^[0-9a-fA-F]{24}$/.test(id);
}

/**
 * Validates if a string contains a valid email format
 * 
 * @param {string} email - The email to validate
 * @returns {boolean} - Whether the email is valid
 */
function isValidEmail(email) {
  if (!email || typeof email !== 'string') {
    return false;
  }
  
  return /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email);
}

/**
 * Validates if a username is valid (used for registration/updates)
 * 
 * @param {string} username - The username to validate
 * @param {boolean} isAdmin - Whether the user is an admin
 * @returns {object} - Validation result { isValid, message }
 */
function validateUsername(username, isAdmin = false) {
  if (!username || typeof username !== 'string') {
    return { 
      isValid: false, 
      message: 'Username is required and must be a string' 
    };
  }
  
  // Username length validation
  if (username.length < 3 || username.length > 30) {
    return { 
      isValid: false, 
      message: 'Username must be between 3 and 30 characters' 
    };
  }
  
  // Check for valid characters - only lowercase letters, numbers and underscores
  if (!/^[a-z0-9_]+$/.test(username)) {
    return { 
      isValid: false, 
      message: 'Username can only contain lowercase letters, numbers, and underscores' 
    };
  }
  
  // Check for admin/moderator terms for non-admin users
  if (!isAdmin && (/admin/i.test(username) || /moderator/i.test(username))) {
    return { 
      isValid: false, 
      message: 'Username cannot contain "admin" or "moderator"' 
    };
  }
  
  return { isValid: true };
}

/**
 * Validates if a password meets security requirements
 * 
 * @param {string} password - The password to validate
 * @returns {object} - Validation result { isValid, message }
 */
function validatePassword(password) {
  if (!password || typeof password !== 'string') {
    return { 
      isValid: false, 
      message: 'Password is required and must be a string' 
    };
  }
  
  // Password length validation
  if (password.length < 8) {
    return { 
      isValid: false, 
      message: 'Password must be at least 8 characters long' 
    };
  }
  
  // Password complexity validation
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  
  if (!(hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar)) {
    return { 
      isValid: false, 
      message: 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character' 
    };
  }
  
  return { isValid: true };
}

/**
 * Validates contact details (usernames for social platforms)
 * Allows only alphanumeric characters, underscores, hyphens, and periods
 * 
 * @param {string} contactValue - The contact value to validate
 * @returns {object} - Validation result { isValid, message }
 */
function validateContactValue(contactValue) {
  // Empty values are allowed (user might not have an account)
  if (!contactValue || contactValue.trim() === '') {
    return { isValid: true };
  }
  
  if (typeof contactValue !== 'string') {
    return { 
      isValid: false, 
      message: 'Contact information must be a string' 
    };
  }
  
  // Check for valid characters - only letters, numbers, underscores, hyphens, and periods
  if (!/^[a-zA-Z0-9_\-\.]+$/.test(contactValue)) {
    return { 
      isValid: false, 
      message: 'Contact information can only contain letters, numbers, underscores, hyphens, and periods' 
    };
  }
  
  return { isValid: true };
}

// Use CommonJS exports for compatibility with Node.js backend
// These will be used when importing with require()
module.exports = {
  isValidObjectId,
  isValidEmail,
  validateUsername,
  validatePassword,
  validateContactValue
};